import { getSortedCalendars, getSortedCalendarGroups } from 'owa-calendar-cache';
import type { CalendarEntry, CalendarGroup } from 'owa-graph-schema';
import getAllAccounts from './getAllAccounts';
import getPrimaryAccount from './getPrimaryAccount';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { isMonarchMultipleAccountsEnabled } from 'owa-accounts-store';

interface CalendarPickerCalendars {
    accounts: SingleAccountCalendarPickerGroups[];
    calendarCount: number;
    shownCalendarCount: number;
    selectedCalendarCount: number;
}

interface SingleAccountCalendarPickerGroups {
    identity: string;
    groups: GroupCalendarPickerCalendars[];
    shownCalendarCount: number;
    selectedCalendarCount: number;
}

interface GroupCalendarPickerCalendars {
    group: CalendarGroup;
    calendars: CalendarPickerCalendar[];
}

export interface CalendarPickerCalendar {
    calendar: CalendarEntry;
    selected: boolean;
    shown: boolean;
}

export function getCalendarPickerCalendars(
    pickerExpanded: boolean,
    shouldShowConnectedAccounts: boolean,
    isCalendarSelected: (calendar: CalendarEntry) => boolean,
    isCalendarShownCollapsed?: (calendar: CalendarEntry, userIdentity: string) => boolean
): CalendarPickerCalendars {
    const accounts: SingleAccountCalendarPickerGroups[] = [];

    let totalCalendarCount = 0;
    let totalShownCalendarCount = 0;
    let totalSelectedCalendarCount = 0;

    const identities =
        shouldShowConnectedAccounts &&
        (isHostAppFeatureEnabled('multiAccounts') || isMonarchMultipleAccountsEnabled())
            ? getAllAccounts()
            : [getPrimaryAccount()];
    identities.forEach(identity => {
        const identityGroups: GroupCalendarPickerCalendars[] = [];
        let shownCalendarsForIdentity = 0;
        let selectedCalendarsForIdentity = 0;
        getSortedCalendarGroups(identity).forEach(group => {
            const calendarsForGroup: CalendarPickerCalendar[] = [];
            const calendars = getSortedCalendars(identity, group.serverGroupId);
            totalCalendarCount += calendars.length;
            calendars.forEach(calendar => {
                const selected = isCalendarSelected(calendar);
                if (selected) {
                    selectedCalendarsForIdentity++;
                }
                const shown =
                    selected || pickerExpanded || isCalendarShownCollapsed?.(calendar, identity);
                if (shown) {
                    shownCalendarsForIdentity++;
                    calendarsForGroup.push({
                        calendar,
                        selected,
                        shown,
                    });
                }
            });
            identityGroups.push({
                group,
                calendars: calendarsForGroup,
            });
        });

        totalShownCalendarCount += shownCalendarsForIdentity;
        totalSelectedCalendarCount += selectedCalendarsForIdentity;

        accounts.push({
            identity,
            groups: identityGroups,
            shownCalendarCount: shownCalendarsForIdentity,
            selectedCalendarCount: selectedCalendarsForIdentity,
        });
    });

    return {
        accounts,
        calendarCount: totalCalendarCount,
        shownCalendarCount: totalShownCalendarCount,
        selectedCalendarCount: totalSelectedCalendarCount,
    };
}
